import { useState, useEffect, useRef } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { Plus, X, Check, ChevronDown, ChevronUp, MapPin, Trash2 } from 'lucide-react'
import { useFeatureFlags } from '../../../lib/featureFlags'

// ── Pre-built checklist ───────────────────────────────────
const CHECKLIST = {
  '12+': {
    label: '12+ Months Out',
    tasks: [
      { id:'b1', title:'Set your total wedding budget',        link:null, onSwitch:'budget', linkLabel:'Budget'      },
      { id:'b2', title:'Choose your wedding date',             link:'/dashboard/events',              linkLabel:'Events'      },
      { id:'b3', title:'Create all your wedding events',       link:'/dashboard/events',              linkLabel:'Events'      },
      { id:'b4', title:'Start building your guest list',       link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'b5', title:'Book your ceremony & reception venue', link:'/dashboard/aisle/c/venue',        linkLabel:'Find on Aisle' },
      { id:'b6', title:'Begin dress / suit shopping',          link:'/dashboard/aisle/c/bridal_fashion', linkLabel:'Find on Aisle' },
    ]
  },
  '6-9': {
    label: '6–9 Months Out',
    tasks: [
      { id:'c1', title:'Design and send Save the Dates',       link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'c2', title:'Book photographer & videographer', link:'/dashboard/aisle/c/photographer', linkLabel:'Find on Aisle' },
      { id:'c3', title:'Choose and contact caterers',      link:'/dashboard/aisle/c/caterer',      linkLabel:'Find on Aisle' },
      { id:'c4', title:'Create your gift registry',            link:'/dashboard/registry',            linkLabel:'Registry'    },
      { id:'c5', title:'Start your love story for guests',     link:'/dashboard/profile',             linkLabel:'Our Profile' },
      { id:'c6', title:'Set dress codes for each event',       link:'/dashboard/events',              linkLabel:'Events'      },
    ]
  },
  '3-6': {
    label: '3–6 Months Out',
    tasks: [
      { id:'d1', title:'Send formal invitations',              link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'d2', title:'Finalise guest list',                  link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'d3', title:'Build the event programme' },
      { id:'d4', title:'Arrange transportation for guests' },
      { id:'d5', title:'Book hair and makeup artists',         link:'/dashboard/aisle/c/makeup',      linkLabel:'Find on Aisle' },
    ]
  },
  '1-3': {
    label: '1–3 Months Out',
    tasks: [
      { id:'e1', title:'Write your vows & speeches' },
      { id:'e2', title:'Confirm all vendors',                  link:'/dashboard/aisle',               linkLabel:'Okoiya Aisle' },
      { id:'e3', title:'Review & finalise budget',             link:null, onSwitch:'budget', linkLabel:'Budget'      },
      { id:'e4', title:'Send final guest counts to caterer' },
      { id:'e5', title:'Prepare wedding day timeline' },
    ]
  },
  'final': {
    label: 'Final Month',
    tasks: [
      { id:'f1', title:'Confirm final guest RSVPs',            link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'f2', title:'Set up guest check-in',                link:'/dashboard/guests',              linkLabel:'Guests'      },
      { id:'f3', title:'Start logging wedding gifts',          link:'/dashboard/registry',            linkLabel:'Registry'    },
      { id:'f4', title:'Write thank you notes list' },
      { id:'f5', title:'Return any rented items' },
    ]
  },
}
const BUCKET_ORDER = ['12+','6-9','3-6','1-3','final']
const QUICK_WIN_IDS = ['b1','b3','b4'] // Set budget, Create events, Build guest list

// A task points into Okoiya Aisle when its link lives under /dashboard/aisle.
const isAisleLink = (to) => typeof to === 'string' && to.startsWith('/dashboard/aisle')

function getBucketForDays(days) {
  if (days > 270) return '12+'
  if (days > 180) return '6-9'
  if (days > 90)  return '3-6'
  if (days > 30)  return '1-3'
  return 'final'
}

// ── Destination pill ──────────────────────────────────────
function DestPill({ label, to, onSwitch, navigate, onSwitchTab, aisleOn }) {
  if (!label) return null
  // Hide Aisle destinations entirely when Okoiya Aisle is switched off in admin.
  if (!aisleOn && isAisleLink(to)) return null
  return (
    <button onClick={e=>{ e.stopPropagation(); if(onSwitch&&onSwitchTab) onSwitchTab(onSwitch); else if(to) navigate(to) }}
      style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#6B5B52', background:'#F0EBE3', border:'none', borderRadius:4, padding:'2px 8px', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, transition:'background 0.15s' }}
      onMouseEnter={e=>e.currentTarget.style.background='rgba(179,107,85,0.15)'}
      onMouseLeave={e=>e.currentTarget.style.background='#F0EBE3'}>
      {label} →
    </button>
  )
}

// ── Task row ─────────────────────────────────────────────
function TaskRow({ task, completed, onToggle, isOverdue, isCustom, onDelete, navigate, onSwitchTab, aisleOn }) {
  const done = completed.includes(task.id)
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.45rem 0', borderLeft: isOverdue&&!done ? '2px solid rgba(217,119,6,0.5)' : 'none', paddingLeft: isOverdue&&!done ? '0.5rem' : 0, opacity:done?0.55:1, transition:'opacity 0.3s' }}>
      <button onClick={()=>onToggle(task.id)}
        style={{ width:20, height:20, flexShrink:0, borderRadius:5, border:`2px solid ${done?'#B36B55':'#D1C9C0'}`, background:done?'#B36B55':'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all 0.2s' }}>
        {done && <Check size={11} color="white" strokeWidth={3}/>}
      </button>
      {isOverdue && !done && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      )}
      <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:done?'#9B9B9B':isOverdue?'#4A3728':'#1A1A1A', flex:1, textDecoration:done?'line-through':'none', textDecorationColor:'#BCBCBC', lineHeight:1.4 }}>
        {task.title}
      </span>
      {isOverdue && !done && (
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'#B45309', background:'rgba(217,119,6,0.1)', borderRadius:100, padding:'1px 6px', flexShrink:0 }}>Overdue</span>
      )}
      {!isCustom && <DestPill label={task.linkLabel} to={task.link} onSwitch={task.onSwitch} navigate={navigate} onSwitchTab={onSwitchTab} aisleOn={aisleOn}/>}
      <button title="Delete task" onClick={e=>{ e.stopPropagation(); onDelete(task, isCustom) }}
        style={{ width:24, height:24, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:'none', border:'none', cursor:'pointer', color:'#C9C1B8', borderRadius:5, transition:'color 0.15s, background 0.15s' }}
        onMouseEnter={e=>{ e.currentTarget.style.color='#C0392B'; e.currentTarget.style.background='rgba(192,57,43,0.08)' }}
        onMouseLeave={e=>{ e.currentTarget.style.color='#C9C1B8'; e.currentTarget.style.background='none' }}>
        <Trash2 size={13}/>
      </button>
    </div>
  )
}

// ── Inline add form ───────────────────────────────────────
function AddTaskForm({ onAdd, onCancel }) {
  const [val, setVal] = useState('')
  const ref = useRef()
  useEffect(()=>{ ref.current?.focus() },[])
  function submit() { if(val.trim()) { onAdd(val.trim()); setVal('') } }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', paddingTop:'0.5rem' }}>
      <div style={{ display:'flex', gap:'0.5rem' }}>
        <input ref={ref} value={val} onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter') submit(); if(e.key==='Escape') onCancel() }}
          placeholder="Task description…"
          style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', padding:'0.45rem 0.75rem', border:'1.5px solid #9BAF9B', borderRadius:'0.5rem', outline:'none' }}/>
        <button onClick={submit} style={{ background:'#B36B55', color:'white', border:'none', borderRadius:'0.5rem', padding:'0.45rem 0.875rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700 }}>Add</button>
      </div>
      <button onClick={onCancel} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', textAlign:'left', padding:0 }}>Cancel</button>
    </div>
  )
}

// ── Section ───────────────────────────────────────────────
function BucketSection({ bucket, label, tasks, customTasks, completed, isCurrent, isPast, weddingDateSet, onToggle, onAddCustom, onDeleteTask, navigate, onSwitchTab, aisleOn }) {
  const [open, setOpen]   = useState(isCurrent)
  const [adding, setAdding] = useState(false)

  const allTasks   = [...tasks, ...customTasks]
  const doneCount  = allTasks.filter(t=>completed.includes(t.id)).length
  const total      = allTasks.length
  const pct        = total>0 ? Math.round((doneCount/total)*100) : 0
  const sectionDone = doneCount===total && total>0

  let counterColor = '#8C7B70'
  if (isCurrent) counterColor = sectionDone?'#3D8B5E':pct===0?'#B45309':'#B36B55'
  else if (isPast && doneCount<total) counterColor = '#B91C1C'

  // Incomplete first, complete last — pre-built then custom within each group.
  const sorted = [...allTasks.filter(t=>!completed.includes(t.id)), ...allTasks.filter(t=>completed.includes(t.id))]

  return (
    <div style={{ background:'white', border:'1px solid #E5E0D8', borderLeft: sectionDone?'3px solid rgba(82,163,105,0.6)':isCurrent?'3px solid #B36B55':'1px solid #E5E0D8', borderRadius:'0.875rem', overflow:'visible' }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', gap:'0.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'#1A1A1A' }}>{label}</span>
          {isCurrent && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'#B36B55', background:'rgba(179,107,85,0.1)', borderRadius:100, padding:'2px 8px' }}>Current</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:counterColor }}>{sectionDone&&'✓ '}{doneCount}/{total} · {pct}%</span>
          {open?<ChevronUp size={14} color="#BCBCBC"/>:<ChevronDown size={14} color="#BCBCBC"/>}
        </div>
      </button>
      {open && (
        <div style={{ padding:'0 1rem 0.875rem', borderTop:'1px solid #F3F0EA' }}>
          {sorted.map(task=>(
            <TaskRow key={task.id} task={task} completed={completed} onToggle={onToggle}
              isOverdue={isPast && weddingDateSet && !completed.includes(task.id)}
              isCustom={customTasks.some(ct=>ct.id===task.id)}
              onDelete={onDeleteTask} navigate={navigate} onSwitchTab={onSwitchTab} aisleOn={aisleOn}/>
          ))}
          {adding
            ? <AddTaskForm onAdd={text=>{onAddCustom(bucket,text);setAdding(false)}} onCancel={()=>setAdding(false)}/>
            : <button onClick={()=>setAdding(true)} style={{ display:'flex', alignItems:'center', gap:'0.35rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', padding:'0.375rem 0', marginTop:'0.25rem' }}>
                <Plus size={13}/> Add custom task
              </button>
          }
        </div>
      )}
    </div>
  )
}

// ── Main export ───────────────────────────────────────────
export default function TodoTab({ onSwitchTab }) {
  const { wedding }   = useWedding()
  const { flags }     = useFeatureFlags()
  const aisleOn       = flags.aisle !== false
  const navigate      = useNavigate()
  const [completed, setCompleted]     = useState([])
  const [customTasks, setCustomTasks] = useState({})
  const [hidden, setHidden]           = useState([])   // dismissed pre-built task ids
  const [dismissedAll, setDismissedAll] = useState(false)

  const daysLeft = wedding?.wedding_date
    ? Math.ceil((new Date(wedding.wedding_date)-new Date())/(1000*60*60*24))
    : null
  const weddingDateSet  = !!wedding?.wedding_date
  const currentBucket   = weddingDateSet ? getBucketForDays(daysLeft) : '12+'

  // Visible pre-built tasks per bucket (hidden ones removed)
  const visibleTasks = (bucket) => CHECKLIST[bucket].tasks.filter(t => !hidden.includes(t.id))

  const allPreIds   = BUCKET_ORDER.flatMap(b=>visibleTasks(b).map(t=>t.id))
  const allCustIds  = Object.values(customTasks).flat().map(t=>t.id)
  const totalTasks  = allPreIds.length + allCustIds.length
  const doneCount   = completed.filter(id=>allPreIds.includes(id)||allCustIds.includes(id)).length
  const overallPct  = totalTasks>0?Math.round((doneCount/totalTasks)*100):0
  const allDone     = doneCount===totalTasks && totalTasks>0

  const quickWinTasks = BUCKET_ORDER.flatMap(b=>visibleTasks(b)).filter(t=>QUICK_WIN_IDS.includes(t.id))
  const quickWinsDone = quickWinTasks.every(t=>completed.includes(t.id))

  useEffect(()=>{
    if(!wedding?.id) return
    async function load() {
      // Resilient select — falls back if the todo_hidden column isn't migrated yet.
      let res = await supabase.from('weddings').select('todo_completed, todo_hidden').eq('id',wedding.id).maybeSingle()
      if (res.error) res = await supabase.from('weddings').select('todo_completed').eq('id',wedding.id).maybeSingle()
      const w = res.data
      if(w?.todo_completed) setCompleted(w.todo_completed)
      if(Array.isArray(w?.todo_hidden)) setHidden(w.todo_hidden)
      const { data:ct } = await supabase.from('custom_tasks').select('*').eq('wedding_id',wedding.id).order('created_at')
      if(ct) {
        const byBucket={}
        ct.forEach(t=>{ if(!byBucket[t.bucket]) byBucket[t.bucket]=[]; byBucket[t.bucket].push({id:t.id,title:t.title}) })
        setCustomTasks(byBucket)
      }
    }
    load()
  },[wedding?.id])

  async function toggleTask(taskId) {
    const next = completed.includes(taskId)?completed.filter(id=>id!==taskId):[...completed,taskId]
    setCompleted(next)
    await supabase.from('weddings').update({todo_completed:next}).eq('id',wedding.id)
  }

  async function addCustomTask(bucket, title) {
    const { data } = await supabase.from('custom_tasks').insert({wedding_id:wedding.id,bucket,title}).select().single()
    if(data) setCustomTasks(prev=>({...prev,[bucket]:[...(prev[bucket]||[]),{id:data.id,title:data.title}]}))
  }

  // Unified delete — custom tasks are removed from the DB; pre-built tasks are
  // hidden for this wedding (reversible via "Restore default tasks").
  async function deleteTask(task, isCustom) {
    if (isCustom) {
      await supabase.from('custom_tasks').delete().eq('id',task.id)
      setCustomTasks(prev=>{ const n={};Object.entries(prev).forEach(([b,ts])=>{n[b]=ts.filter(t=>t.id!==task.id)});return n })
      setCompleted(prev=>prev.filter(id=>id!==task.id))
    } else {
      const next = [...hidden, task.id]
      setHidden(next)
      supabase.from('weddings').update({ todo_hidden: next }).eq('id',wedding.id).then(()=>{},()=>{})
    }
  }

  function restoreDefaults() {
    setHidden([])
    supabase.from('weddings').update({ todo_hidden: [] }).eq('id',wedding.id).then(()=>{},()=>{})
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

      {/* Header */}
      <div>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', color:'#1A1A1A', margin:'0 0 0.2rem', letterSpacing:'-0.03em' }}>Wedding Checklist</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#9B9B9B', margin:0 }}>Everything you need before the big day</p>
        {hidden.length > 0 && (
          <button onClick={restoreDefaults} style={{ marginTop:'0.4rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#B36B55', padding:0 }}>
            ↺ Restore default tasks ({hidden.length} hidden)
          </button>
        )}
      </div>

      {/* Overall progress */}
      <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#1A1A1A' }}>Overall Progress</span>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B' }}>{doneCount}/{totalTasks} · {overallPct}%</span>
        </div>
        <div style={{ height:6, background:'#F0EBE3', borderRadius:100, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${overallPct}%`, background:'#B36B55', borderRadius:100, transition:'width 0.4s ease-out' }}/>
        </div>
      </div>

      {/* Current bucket indicator */}
      {weddingDateSet && (
        <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
          <MapPin size={13} color="#B36B55"/>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B' }}>
            You are currently in: <strong style={{ color:'#B36B55' }}>{CHECKLIST[currentBucket].label}</strong>
          </span>
        </div>
      )}

      {/* All done celebration */}
      {allDone && !dismissedAll && (
        <div style={{ background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:'1.25rem', padding:'1.5rem', textAlign:'center', position:'relative' }}>
          <button onClick={()=>setDismissedAll(true)} style={{ position:'absolute', top:10, right:10, background:'none', border:'none', cursor:'pointer', color:'#BCBCBC' }}><X size={16}/></button>
          <svg width="44" height="44" viewBox="0 0 48 48" fill="none" style={{ marginBottom:'0.75rem' }}>
            <circle cx="24" cy="24" r="20" stroke="#B36B55" strokeWidth="1.5" fill="none"/>
            <path d="M15 24l6 6 12-12" stroke="#B36B55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>You are all set! ✓</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0 0 1rem', lineHeight:1.7 }}>Every task is done. Your wedding day is beautifully prepared.</p>
          <button onClick={()=>navigate('/dashboard')} style={{ background:'linear-gradient(135deg,#B36B55,#C4826A)', color:'white', border:'none', borderRadius:100, padding:'0.625rem 1.5rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem' }}>
            View your wedding overview →
          </button>
        </div>
      )}

      {/* Quick Wins */}
      {!quickWinsDone && quickWinTasks.length > 0 && (
        <div style={{ background:'rgba(179,107,85,0.05)', border:'1px solid rgba(179,107,85,0.2)', borderLeft:'3px solid #B36B55', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700, color:'#B36B55', letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 0.15rem' }}>⚡ Quick Wins</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.75rem' }}>Complete these first — each takes under 2 minutes inside Okoiya</p>
          {quickWinTasks.filter(t=>!completed.includes(t.id)).map(task=>(
            <div key={task.id} style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.35rem 0', borderBottom:'1px solid rgba(179,107,85,0.08)' }}>
              <button onClick={()=>toggleTask(task.id)} style={{ width:18, height:18, flexShrink:0, borderRadius:4, border:'2px solid #D1C9C0', background:'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}/>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', flex:1 }}>{task.title}</span>
              <button onClick={()=>{ if(task.onSwitch&&onSwitchTab) onSwitchTab(task.onSwitch); else if(task.link) navigate(task.link) }} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:'#B36B55', background:'rgba(179,107,85,0.1)', border:'none', borderRadius:100, padding:'3px 10px', cursor:'pointer', whiteSpace:'nowrap' }}>Do it →</button>
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      {BUCKET_ORDER.map(bucket=>{
        const idx         = BUCKET_ORDER.indexOf(bucket)
        const curIdx      = BUCKET_ORDER.indexOf(currentBucket)
        const isCurrent   = bucket===currentBucket
        const isPast      = idx>curIdx
        const bucketCustom = customTasks[bucket]||[]
        return (
          <BucketSection key={bucket} bucket={bucket}
            label={CHECKLIST[bucket].label}
            tasks={visibleTasks(bucket)}
            customTasks={bucketCustom}
            completed={completed}
            isCurrent={isCurrent}
            isPast={isPast}
            weddingDateSet={weddingDateSet}
            onToggle={toggleTask}
            onAddCustom={addCustomTask}
            onDeleteTask={deleteTask}
            navigate={navigate}
            onSwitchTab={onSwitchTab}
            aisleOn={aisleOn}/>
        )
      })}
    </div>
  )
}
